import { companyIsoDay } from './dates';
import { saveBlob } from './download';

/**
 * CSV export (B6, owner rulings 2026-10-25): "Download CSV" on boards,
 * reports, the Tasks list, Analytics, Budgets and admin logs — built in
 * the browser from what is on screen, so never more than the viewer can
 * already see. DM / Auditor / Admin / Executive only (canExportCsv). No
 * dependency: plain CSV that Excel opens (UTF-8 with a BOM).
 */

export type CsvCell = string | number | boolean | null | undefined;

/** Roles that may download (owner ruling 2026-10-25: store teams don't get the button). */
export function canExportCsv(role: string | null | undefined): boolean {
  return role === 'DM' || role === 'Auditor' || role === 'Admin' || role === 'Executive';
}

/**
 * One cell, quoted when needed. Text a spreadsheet would run as a formula
 * (starting with = + - @ — also after spaces or in fullwidth form — or a
 * tab / line break) gets a leading apostrophe — CSV injection (OWASP). Plain numbers stay numbers, so a
 * negative KPI change (-3) is still a number.
 */
export function csvCell(value: CsvCell): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  let s = value;
  // Also after leading spaces (importers that trim), and the fullwidth forms
  // some locales convert (security review 2026-10-25).
  if (/^[\s\u00a0\u3000]*[=+\-@\uff1d\uff0b\uff0d\uff20]|^[\t\r\n]/.test(s)) s = `'${s}`;
  // `;` quoted too: Excel in a semicolon-separator locale splits on it.
  return /[",;\r\n]/.test(s) || s !== s.trim() ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(header: readonly string[], rows: readonly (readonly CsvCell[])[]): string {
  return [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

/** "auditplus-board-2026-10-25.csv" — the company (Eastern) date, letters/digits/dashes only. */
export function csvFileName(what: string, now: Date = new Date()): string {
  const day = companyIsoDay(now);
  const slug = what.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'export';
  return `auditplus-${slug}-${day}.csv`;
}

/** Hands the CSV to the browser (UTF-8 with a BOM, so Excel reads accents). */
export function downloadCsv(fileName: string, header: readonly string[], rows: readonly (readonly CsvCell[])[]): void {
  saveBlob(fileName, new Blob(['\ufeff', toCsv(header, rows)], { type: 'text/csv;charset=utf-8' }));
}

/** The apostrophe csvCell puts in front of formula-like text — taken off again on import. */
const GUARDED = /^'(?=[\s 　]*[=+\-@＝＋－＠]|[\t\r\n])/;

/**
 * Read CSV text (RFC 4180, as Excel saves it): a UTF-8 BOM is dropped,
 * quoted cells may hold commas, line breaks and "" for a quote, lines end
 * in CRLF or LF, a trailing blank line is ignored. The apostrophe our own
 * export adds for formula safety (csvCell) is removed. No dependency (B8
 * budget import, 2026-10-25).
 */
export function parseCsv(text: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let wasQuoted = false;
  const endCell = () => {
    row.push(cell.replace(GUARDED, ''));
    cell = '';
    wasQuoted = false;
  };
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && cell === '') {
      quoted = true;
      wasQuoted = true;
    } else if (ch === ',') endCell();
    else if (ch === '\r' || ch === '\n') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      endCell();
      rows.push(row);
      row = [];
    } else cell += ch;
  }
  if (cell !== '' || wasQuoted || row.length > 0) {
    endCell();
    rows.push(row);
  }
  // Excel pads with empty lines: drop rows that are entirely blank.
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}
